import type { DraftExperimentFormProps } from '@gonfalon/experiments/types';
import { LayerPatchInstructionKind } from '@gonfalon/instructions/instructionTypes/layerPatchInstruction';
import { useUpdateLayer } from '@gonfalon/rest-api/hooks/useUpdateLayer';
import { isEqual } from 'lodash';

type UpdateLayerForDraftExperimentProps = {
  projectKey: string;
  environmentKey: string;
  experimentKey: string;
};

export const useUpdateLayerForDraftExperiment = (props: UpdateLayerForDraftExperimentProps) => {
  const { projectKey, environmentKey, experimentKey } = props;
  const { mutateAsync: updateLayer } = useUpdateLayer();

  const updateLayerBeforeIteration = async (
    updatedValues: DraftExperimentFormProps,
    defaultValues: DraftExperimentFormProps,
  ): Promise<boolean> => {
    const currentConfig = updatedValues.layerConfig;
    const previousConfig = defaultValues.layerConfig;

    // Handle layer removal when hasLayers is turned off
    if (!updatedValues.hasLayers && previousConfig?.key) {
      await updateLayer({
        projectKey,
        layerKey: previousConfig.key,
        body: {
          environmentKey,
          instructions: [
            {
              kind: LayerPatchInstructionKind.REMOVE_EXPERIMENT,
              experimentKey,
            },
          ],
        },
      });
      return true;
    }

    const instructions = [];

    // Check for name changes
    if (previousConfig && !isEqual(currentConfig?.name, previousConfig.name)) {
      instructions.push({
        kind: LayerPatchInstructionKind.UPDATE_NAME,
        name: currentConfig?.name,
      });
    }

    // Check for description changes
    if (currentConfig?.description && !isEqual(currentConfig?.description, previousConfig?.description)) {
      instructions.push({
        kind: LayerPatchInstructionKind.UPDATE_DESCRIPTION,
        description: currentConfig?.description,
      });
    }

    // Add reservation update if it has changed
    if (!isEqual(currentConfig?.reservationPercent, previousConfig?.reservationPercent)) {
      instructions.push({
        kind: LayerPatchInstructionKind.UPDATE_EXPERIMENT_RESERVATION,
        experimentKey,
        reservationPercent: currentConfig?.reservationPercent,
      });
    }

    // Remove experiment from old layer if layer key has changed
    let updated = false;

    if (previousConfig?.key && !isEqual(currentConfig?.key, previousConfig.key)) {
      await updateLayer({
        projectKey,
        layerKey: previousConfig.key,
        body: {
          environmentKey,
          instructions: [
            {
              kind: LayerPatchInstructionKind.REMOVE_EXPERIMENT,
              experimentKey,
            },
          ],
        },
      });
      updated = true;
    }

    if (instructions.length > 0 && currentConfig?.key) {
      await updateLayer({
        projectKey,
        layerKey: currentConfig?.key,
        body: {
          environmentKey,
          instructions,
        },
      });
      updated = true;
    }

    return updated;
  };

  const revertLayerUpdate = async (
    updatedValues: DraftExperimentFormProps,
    defaultValues: DraftExperimentFormProps,
  ) => {
    const updatedConfig = updatedValues.hasLayers ? updatedValues.layerConfig : undefined;
    const previousConfig = defaultValues.layerConfig;
    const layerKeyChanged = updatedConfig?.key !== previousConfig?.key;

    if (updatedConfig?.key && layerKeyChanged) {
      await updateLayer({
        projectKey,
        layerKey: updatedConfig.key,
        body: {
          environmentKey,
          instructions: [
            {
              kind: LayerPatchInstructionKind.REMOVE_EXPERIMENT,
              experimentKey,
            },
          ],
        },
      });
    }

    if (!previousConfig?.key) {
      return;
    }

    const instructions = [];

    if (!layerKeyChanged && !isEqual(updatedConfig?.name, previousConfig.name)) {
      instructions.push({
        kind: LayerPatchInstructionKind.UPDATE_NAME,
        name: previousConfig.name,
      });
    }

    if (!layerKeyChanged && !isEqual(updatedConfig?.description ?? '', previousConfig.description ?? '')) {
      instructions.push({
        kind: LayerPatchInstructionKind.UPDATE_DESCRIPTION,
        description: previousConfig.description ?? '',
      });
    }

    if (layerKeyChanged || !isEqual(updatedConfig?.reservationPercent, previousConfig.reservationPercent)) {
      instructions.push({
        kind: LayerPatchInstructionKind.UPDATE_EXPERIMENT_RESERVATION,
        experimentKey,
        reservationPercent: previousConfig.reservationPercent,
      });
    }

    if (instructions.length > 0) {
      await updateLayer({
        projectKey,
        layerKey: previousConfig.key,
        body: {
          environmentKey,
          instructions,
        },
      });
    }
  };

  return {
    updateLayerBeforeIteration,
    revertLayerUpdate,
  };
};
